import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule,themeAlpine } from "ag-grid-community";
import { Search,X,Download } from "lucide-react";
import { formatEuroCurrency } from "../../lib/currency";
import { formatWithSpace } from "../../lib/currency";
ModuleRegistry.registerModules([AllCommunityModule]);

export default function DynamicAgGrid({ response, height = 360, theme = "light" }) {
  const {data}=response
    const gridRef = useRef(null);
  const [quickFilter, setQuickFilter] = useState("");
  const isDark = theme === "dark";
  const parsedTitle = useMemo(() => {
    if (!response?.title) return {};
    try {
      return JSON.parse(response.title);
    } catch {
      return {};
    }
  }, [response?.title]);

  // const currencyFields = useMemo(() => {
  //   const fields = parsedTitle?.currency_field;
  //   return Array.isArray(fields) ? fields : [];
  // }, [parsedTitle]);

  // const isCurrencyField = (col) =>
  //   currencyFields.some(
  //     (field) => field?.toString().toLowerCase() === col.toString().toLowerCase(),
  //   );


  const isCurrencyField = (col) => col?.toString().toLowerCase().includes("€");

  const isGrowthField = (col) => col?.toString().toLowerCase().includes("%");
  const gridThemeStyle = isDark
    ? {
        "--ag-background-color": "#0b1220",
        "--ag-odd-row-background-color": "#0f172a",
        "--ag-header-background-color": "#111827",
        "--ag-border-color": "#1f2937",
        "--ag-foreground-color": "#e2e8f0",
        "--ag-secondary-foreground-color": "#94a3b8",
        "--ag-header-foreground-color": "#e2e8f0",
        "--ag-row-hover-color": "#111827",
        "--ag-selected-row-background-color": "#1f2937",
        "--ag-input-focus-border-color": "#cc0452",
      }
    : undefined;

  const columnDefs = useMemo(() => {
    if (!data?.columns) return [];

     const columns = [
{
  headerName: "S.No",
  colId: "serialNumber",
  sortable: false,
  filter: false,
  floatingFilter: false,
  resizable: false,
  width: 70,
  minWidth: 70,
  maxWidth: 70,
  pinned: "left",
  lockPosition: "left",
  cellStyle: { 
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  valueGetter: (params) =>
    params.node ? params.node.rowIndex + 1 : "",
}


,
    ...data.columns.map((col) => ({
      headerName: isCurrencyField(col)
        // ? `${col} €`
        ? `${col}`
        : isGrowthField(col) && !col.includes("%")
          ? `${col} %`
          : col,
      field: col,
      sortable: true,
      filter: true,
      floatingFilter: false,
      resizable: true,
      flex: 1,
      minWidth: 120,
      // cellStyle: { padding: '8px' },
   valueFormatter: col.toLowerCase().includes("date") && !col.toLowerCase().includes("validated")

  ? (p) => {

      if (p.value) {

        const date = new Date(p.value);
        const value = String(p.value).trim();

        if (value.includes("/")) {
          return value;
        }

        // Check if the value is just a date (no time)

        const isDateOnly = date.toISOString().split('T')[1] === '00:00:00.000Z';

        return isDateOnly

          ? date.toLocaleDateString() // Only show the date (no time)

          : date.toLocaleString(); // Show both date and time

      }

      return "";

    }

  : isCurrencyField(col)
  ? (p) => {
      if (p.value === null || p.value === undefined || p.value === "") return "";
      if (typeof p.value === "string" && p.value.includes("€")) return p.value;
      const numeric = typeof p.value === "number" ? p.value : Number(p.value);
      if (Number.isNaN(numeric)) return p.value;
      return `${formatEuroCurrency(numeric, "fr", 3, "", "")}`;
    }
  : isGrowthField(col)
  ? (p) => {
      if (p.value === null || p.value === undefined || p.value === "") return "";
      if (typeof p.value === "string" && p.value.includes("%")) return p.value;
      const numeric = typeof p.value === "number" ? p.value : Number(p.value);
      if (Number.isNaN(numeric)) return p.value;
      return `${formatWithSpace(numeric, "fr", 3, "", "")} %`;
    }
  : col.toLowerCase().includes("value")
  ? (p) =>
      typeof p.value === "number"
        ? p.value.toLocaleString() 
        : p.value

  : undefined,

 
    }))
  ];

  return columns;
  }, [data]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: false,
      resizable: true,
      suppressSizeToFit: false,
      autoHeight: false,
      cellStyle: { 
        padding: "6px 21px",
        lineHeight: "20px",
        height: "100%",
        display: "flex",
        alignItems: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      headerClass: 'ag-header-cell-custom',
    }),
    []
  );

  const onExportCSV = () => {
    gridRef.current.api.exportDataAsCsv({ 
      fileName: "report.csv",
      skipColumnGroupHeaders: true,
      skipColumnHeaders: false,
      suppressQuotes: false
    });
  };

  // ✅ Clear column filters + global search
  const onClearFilters = () => {
    setQuickFilter("");
    gridRef.current.api.setFilterModel(null);
  };

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {/* Header Section */}
 
  <div className="flex flex-col justify-between gap-3 p-1 px-2">
       <div className="space-y-1">
         {/* <h2 className="text-xl font-semibold text-gray-800">Results</h2> */}
        
       </div>
       {/* Controls Section */}
       <div className="flex flex-wrap items-center gap-2">
         {/* Search Input */}
         <div className="relative flex-1 min-w-[200px]">
           <Search
             className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? "text-slate-500" : "text-gray-400"}`}
             size={16}
           />
           <input
             type="text"
             placeholder="Search all columns..."
             value={quickFilter}
             onChange={(e) => setQuickFilter(e.target.value)}
             className={`w-full rounded-lg border px-3 py-2 pl-10 text-sm ${
               isDark
                 ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
                 : "border-gray-300 bg-white text-slate-800 placeholder:text-slate-400"
             }`}
           />
         </div>
         {/* Action Buttons */}
         <div className="flex items-center gap-2">
           <button
              onClick={onClearFilters}
              disabled={
              !quickFilter &&
              Object.keys(gridRef.current?.api?.getFilterModel?.() || {}).length === 0
              }
              aria-label="Clear filters"
              title="Clear filters"
              className={`flex items-center justify-center rounded-xl p-2 text-white transition-colors ${
              !quickFilter &&
              Object.keys(gridRef.current?.api?.getFilterModel?.() || {}).length === 0
                ? "bg-gray-400 cursor-not-allowed opacity-50"
                : "bg-[#cc0452] hover:brightness-110"
              }`}
           >
             <X size={16} />
           </button>
           <button
             onClick={onExportCSV}
             aria-label="Export CSV"
             title="Export CSV"
             className="flex items-center justify-center rounded-xl bg-[#cc0452] p-2 text-white transition-colors hover:brightness-110"
           >
             <Download size={16} />
           </button>
         </div>
       </div>
       </div>
      {/* Grid Container */}
      <div
        className="ag-theme-alpine w-full flex-1 overflow-hidden rounded-2xl shadow-sm"
        style={{ height, ...gridThemeStyle }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={data?.rows || []}
          columnDefs={columnDefs}
          theme={themeAlpine}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilter}
          animateRows={true}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          domLayout="normal"
          ensureDomOrder={true}
          suppressCellFocus={false}
          suppressRowHoverHighlight={false}
          onSortChanged={() => {
          gridRef.current.api.refreshCells({
      columns: ["serialNumber"],
      force: true,
    })}}
          onFilterChanged={() => {
          gridRef.current.api.refreshCells({
          columns: ["serialNumber"],
          force: true,
        });
      }}
          suppressMenuHide={false}
          rowHeight={40} // Increased row height
          headerHeight={45} // Increased header height
        />
      </div>
    </div>
  );
}
